/**
 * Tenant-side System Library hooks — PR-J.
 *
 * Read-only access to published system Library content
 * (organization_id IS NULL AND status='published') with I-M6.7
 * redaction baked in by the backend (see
 * packages/api/src/routes/v1/library/system.ts).
 *
 * Tenants see system rows badged "System" in their Library; this hook
 * is the data feed for that view. Mutation is impossible for tenants —
 * sysadmin owns the lifecycle.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';

const BASE = '/library/system';

// Wire shapes — match the redacted server schemas. publishedByLabel is
// always "Platform Admin" (no real user uuids ever leaked to tenants).

export interface SystemLibFolder {
  id: string;
  source: 'system';
  name: string;
  slug: string | null;
  description: string | null;
  parentId: string | null;
  sortOrder: number;
  icon: string | null;
  color: string | null;
  sensitivity: string;
  exportControl: string;
  publishedAt: string | null;
  publishedByLabel: string;
  createdAt: string;
  updatedAt: string;
}

export interface SystemLibArticleListItem {
  id: string;
  source: 'system';
  title: string;
  slug: string | null;
  excerpt: string | null;
  folderId: string | null;
  parentId: string | null;
  publishedAt: string | null;
  publishedByLabel: string;
  authorLabel: string;
  viewCount: number;
  sensitivity: string;
  exportControl: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface SystemLibArticleDetail extends SystemLibArticleListItem {
  content: string;
  contentText: string;
}

export interface SystemLibTag {
  id: string;
  source: 'system';
  name: string;
  color: string;
  sortOrder: number;
  publishedAt: string | null;
}

export interface SystemLibDocument {
  id: string;
  source: 'system';
  folderId: string | null;
  title: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  ingestionStatus: string;
  documentType: string | null;
  description: string | null;
  chunkCount: number | null;
  publishedAt: string | null;
  publishedByLabel: string;
  sensitivity: string;
  exportControl: string;
  createdAt: string;
  updatedAt: string;
}

export interface SystemLibSource {
  id: string;
  source: 'system';
  connectorKind: string;
  displayName: string;
  syncSchedule: string;
  syncEnabled: boolean;
  publishedAt: string | null;
  publishedByLabel: string;
  createdAt: string;
  updatedAt: string;
}

const KEYS = {
  folders: ['tenant-system-library', 'folders'] as const,
  articles: (folderId?: string) =>
    ['tenant-system-library', 'articles', folderId ?? 'all'] as const,
  article: (id: string) => ['tenant-system-library', 'article', id] as const,
  tags: ['tenant-system-library', 'tags'] as const,
  documents: (folderId?: string) =>
    ['tenant-system-library', 'documents', folderId ?? 'all'] as const,
  sources: ['tenant-system-library', 'sources'] as const,
};

export function useTenantSystemFolders() {
  return useQuery<{ data: SystemLibFolder[] }, Error>({
    queryKey: KEYS.folders,
    queryFn: () => apiClient.get<{ data: SystemLibFolder[] }>(`${BASE}/folders`),
  });
}

export function useTenantSystemArticles(folderId?: string) {
  const suffix = folderId ? `?folderId=${folderId}` : '';
  return useQuery<{ data: SystemLibArticleListItem[] }, Error>({
    queryKey: KEYS.articles(folderId),
    queryFn: () =>
      apiClient.get<{ data: SystemLibArticleListItem[] }>(`${BASE}/articles${suffix}`),
  });
}

export function useTenantSystemArticle(id: string | null | undefined) {
  return useQuery<SystemLibArticleDetail, Error>({
    queryKey: id ? KEYS.article(id) : ['__skip__'],
    queryFn: () => apiClient.get<SystemLibArticleDetail>(`${BASE}/articles/${id}`),
    enabled: !!id,
  });
}

export function useTenantSystemTags() {
  return useQuery<{ data: SystemLibTag[] }, Error>({
    queryKey: KEYS.tags,
    queryFn: () => apiClient.get<{ data: SystemLibTag[] }>(`${BASE}/tags`),
  });
}

export function useTenantSystemDocuments(folderId?: string) {
  const suffix = folderId ? `?folderId=${folderId}` : '';
  return useQuery<{ data: SystemLibDocument[] }, Error>({
    queryKey: KEYS.documents(folderId),
    queryFn: () =>
      apiClient.get<{ data: SystemLibDocument[] }>(`${BASE}/documents${suffix}`),
  });
}

export function useTenantSystemSources() {
  return useQuery<{ data: SystemLibSource[] }, Error>({
    queryKey: KEYS.sources,
    queryFn: () => apiClient.get<{ data: SystemLibSource[] }>(`${BASE}/sources`),
  });
}
